import { cn } from '@/lib/utils'
import { Button } from '@/shared/ui/Button'

/**
 * Tela que interrompe a trilha do Level Play — sem vidas, tempo esgotado,
 * conclusão, bloqueio, abertura.
 *
 * Derivado da estrutura de `shared/ui/Blocker.tsx`, que já resolvia isto de
 * forma correta: ícone por import direto, `Button` do módulo, contêiner
 * centralizado. As telas intersticiais reinventaram um `Card` cada uma em vez de
 * convergir para ele, e foi assim que acumularam `text-gray-500` em corpo,
 * escala divergente entre domínios e altura travada.
 *
 * **Não reusa o próprio `Blocker`** porque ele resolve a mensagem a partir de
 * uma `BlockCondition`. Sem-vidas e tempo-esgotado não têm condição de bloqueio;
 * forçar o encaixe distorceria a interface dele.
 *
 * Mobile-first por construção: largura fluida com teto, sem altura mínima
 * travada, e o conteúdo empilha em coluna única — cabe a 375px sem rolagem
 * horizontal.
 *
 * **Não é um card.** Era montado sobre `Card`, que traz `border border-contour`
 * e canto arredondado por construção, e o resultado destoava das demais telas do
 * Level Play: um retângulo com borda flutuando colado no topo de uma página
 * vazia, enquanto `Blocker` ocupa a área inteira em branco. Aqui a superfície é
 * a própria área de execução — sem borda, sem sombra — e o `flex-1` é o que tira
 * a tela do topo: sem ele o bloco não reivindica a altura da coluna do shell e
 * não há o que centralizar.
 *
 * O `bg-white` é explícito porque a tela intersticial renderiza **fora** de
 * `Screen`, pendurada direto na coluna do shell: sem ele a superfície herda o
 * lilás da página e a tela deixa de casar com `Blocker`.
 */
export interface LevelPlayInterstitialProps {
  /** Ícone da tela — import direto, nunca slug resolvido em runtime. */
  icon: React.ReactNode
  title: string
  /** Texto de apoio. Sempre em `gray-700`: `gray-500` tem 2.45:1 e é reservado a elemento desabilitado. */
  description?: React.ReactNode
  /** Ação de saída. Toda tela intersticial oferece ao menos uma. */
  action?: React.ReactNode
  /** Conteúdo extra entre o texto e a ação — recompensas, progresso. */
  children?: React.ReactNode
  /**
   * Classe da **coluna de conteúdo**, não da superfície: quem precisa de mais
   * largura (a conclusão, com a lista de acertos) alarga a coluna sem transformar
   * a tela num card de novo.
   */
  className?: string
}

export const LevelPlayInterstitial = ({
  icon,
  title,
  description,
  action,
  children,
  className
}: LevelPlayInterstitialProps) => (
  <div className="flex w-full flex-1 flex-col items-center justify-center bg-white px-4 py-10 text-center">
    <div
      className={cn(
        'flex w-full max-w-[400px] flex-col items-center gap-3',
        className
      )}
    >
      <div className="flex items-center justify-center">{icon}</div>

      <h5 className="font-bold text-gray-900 text-xl">{title}</h5>

      {description && (
        <p className="text-gray-700 text-base max-w-[320px]">{description}</p>
      )}

      {children}

      {action && <div className="mt-4 flex w-full flex-col">{action}</div>}
    </div>
  </div>
)

/**
 * Ação padrão de recomeçar, usada por sem-vidas e tempo esgotado. Existe aqui
 * para que as duas telas não repitam a mesma composição de botão.
 */
export const LevelPlayInterstitialRestartAction = ({
  onRestart
}: {
  onRestart: () => void
}) => (
  <Button variant="primary" className="grow" showTrailingIcon={false} onClick={onRestart}>
    Tentar mais uma vez
  </Button>
)
